import { QuestionType } from '@/survey-graphql';

interface MobileUtControlsLayout {
  shouldFloatControls: boolean;
  isCollapsed: boolean;
}

export interface Point {
  x: number;
  y: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface SafeInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

// The task sheet's full-screen dismiss scrim mounts under the finger that opened it; the
// opening tap's synthesized click then lands on the scrim and would instantly re-close the
// sheet. Ignore scrim dismissal within this window of the sheet opening so only a later,
// deliberate tap-outside closes it.
export const MOBILE_TASK_SHEET_DISMISS_GRACE_MS = 400;

export function isWithinTaskSheetDismissGrace(
  openedAt: number,
  now: number
): boolean {
  return now - openedAt < MOBILE_TASK_SHEET_DISMISS_GRACE_MS;
}

const NO_SAFE_INSETS: SafeInsets = { top: 0, right: 0, bottom: 0, left: 0 };

export function deriveMobileUtControlsLayout(input: {
  isMobileMode: boolean;
  contentUrl: string | null;
  mobileAppEmbedUrl: string | null;
  isMobileAppPreloading: boolean;
}): MobileUtControlsLayout {
  const contentIsShowing = Boolean(
    input.contentUrl ||
    (input.mobileAppEmbedUrl && !input.isMobileAppPreloading)
  );
  const shouldFloatControls = input.isMobileMode && contentIsShowing;
  return { shouldFloatControls, isCollapsed: shouldFloatControls };
}

export function shouldAwaitTaskCompletion(input: {
  isMobileMode: boolean;
  questionType: string | null | undefined;
  hasClosedTaskBar: boolean;
}): boolean {
  const isTaskQuestion =
    input.questionType?.toLowerCase() === QuestionType.Task.toLowerCase();
  return input.isMobileMode && isTaskQuestion && !input.hasClosedTaskBar;
}

export function clampPillPosition(
  pos: Point,
  bounds: Size,
  pillSize: Size,
  safeInsets: SafeInsets = NO_SAFE_INSETS
): Point {
  const minX = safeInsets.left;
  const minY = safeInsets.top;
  const maxX = Math.max(minX, bounds.width - pillSize.width - safeInsets.right);
  const maxY = Math.max(
    minY,
    bounds.height - pillSize.height - safeInsets.bottom
  );
  return {
    x: Math.min(Math.max(pos.x, minX), maxX),
    y: Math.min(Math.max(pos.y, minY), maxY),
  };
}
